import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Callout,
  DateField,
  EmptyState,
  Modal,
  Panel,
  SelectField,
  Spinner,
  Table,
  Tag,
  TextField,
} from '@humanforce/mango-ui-react';
import type { OrgBusinessRecord } from '../organisationFixtures';
import type {
  PayFrequency,
  PayGroupDto,
  PayGroupStatus,
  PayPeriodDto,
} from '../organisationApiTypes';
import {
  createPayGroup,
  createPayPeriod,
  listPayGroups,
  listPayPeriods,
  updatePayGroup,
  updatePayPeriod,
} from '../payGroupApi';

interface PayGroupsPanelProps {
  businessRecords: OrgBusinessRecord[];
}

const frequencyOptions = [
  { label: 'Weekly', value: 'weekly' },
  { label: 'Fortnightly', value: 'fortnightly' },
  { label: 'Monthly', value: 'monthly' },
];

const statusOptions = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
];

const formatFrequency = (value: PayFrequency) =>
  frequencyOptions.find((option) => option.value === value)?.label ?? value;

const formatDate = (value: string) =>
  new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(`${value}T00:00:00Z`));

const groupColumns = [
  { field: 'name', header: 'Name', sortable: true },
  { field: 'code', header: 'Code', sortable: true },
  { field: 'frequencyLabel', header: 'Pay Frequency', sortable: true },
  { field: 'payPeriodCount', header: 'Pay Periods', sortable: true },
  { field: 'statusLabel', header: 'Status', sortable: true },
  { field: 'organisationName', header: 'Business Record', sortable: true },
];

const periodColumns = [
  { field: 'periodLabelDisplay', header: 'Label', sortable: true },
  { field: 'dateRange', header: 'Period', sortable: true },
  { field: 'paymentDateDisplay', header: 'Payment Date', sortable: true },
];

interface GroupDraft {
  name: string;
  code: string;
  payFrequency: PayFrequency | '';
}

interface PeriodDraft {
  periodStartDate: string;
  periodEndDate: string;
  paymentDate: string;
  periodLabel: string;
}

const emptyGroupDraft: GroupDraft = { name: '', code: '', payFrequency: '' };
const emptyPeriodDraft: PeriodDraft = {
  periodStartDate: '',
  periodEndDate: '',
  paymentDate: '',
  periodLabel: '',
};

export const PayGroupsPanel = ({ businessRecords }: PayGroupsPanelProps) => {
  const availableRecords = useMemo(
    () => businessRecords.filter((record) => Boolean(record.orgId)),
    [businessRecords],
  );
  const [organisationId, setOrganisationId] = useState(availableRecords[0]?.orgId ?? '');
  const [status, setStatus] = useState<PayGroupStatus>('active');
  const [groups, setGroups] = useState<PayGroupDto[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<PayGroupDto | null>(null);
  const [periods, setPeriods] = useState<PayPeriodDto[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<PayPeriodDto | null>(null);
  const [loading, setLoading] = useState(Boolean(availableRecords.length));
  const [periodsLoading, setPeriodsLoading] = useState(false);
  const [groupError, setGroupError] = useState<string | null>(null);
  const [periodError, setPeriodError] = useState<string | null>(null);
  const [groupRefresh, setGroupRefresh] = useState(0);
  const [periodRefresh, setPeriodRefresh] = useState(0);
  const [groupModal, setGroupModal] = useState<'create' | 'edit' | null>(null);
  const [periodModal, setPeriodModal] = useState<'create' | 'edit' | null>(null);
  const [deactivationTarget, setDeactivationTarget] = useState<PayGroupDto | null>(null);
  const [groupDraft, setGroupDraft] = useState<GroupDraft>(emptyGroupDraft);
  const [periodDraft, setPeriodDraft] = useState<PeriodDraft>(emptyPeriodDraft);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!availableRecords.some((record) => record.orgId === organisationId))
      setOrganisationId(availableRecords[0]?.orgId ?? '');
  }, [availableRecords, organisationId]);

  useEffect(() => {
    let cancelled = false;
    if (!organisationId) {
      setLoading(false);
      setGroups([]);
      return;
    }
    setLoading(true);
    setGroupError(null);
    void listPayGroups(organisationId, status)
      .then((result) => {
        if (cancelled) return;
        setGroups(result);
        setSelectedGroup((current) => result.find((group) => group.payGroupId === current?.payGroupId) ?? null);
      })
      .catch((reason: unknown) => {
        if (!cancelled) setGroupError(reason instanceof Error ? reason.message : 'Failed to load Pay Groups.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [organisationId, status, groupRefresh]);

  const selectedGroupId = selectedGroup?.payGroupId ?? '';
  useEffect(() => {
    let cancelled = false;
    if (!selectedGroupId) {
      setPeriods([]);
      setSelectedPeriod(null);
      setPeriodError(null);
      return;
    }
    setPeriodsLoading(true);
    setPeriodError(null);
    void listPayPeriods(selectedGroupId)
      .then((result) => {
        if (cancelled) return;
        setPeriods(result);
        setSelectedPeriod((current) => result.find((period) => period.payPeriodId === current?.payPeriodId) ?? null);
      })
      .catch((reason: unknown) => {
        if (!cancelled) setPeriodError(reason instanceof Error ? reason.message : 'Failed to load Pay Periods.');
      })
      .finally(() => { if (!cancelled) setPeriodsLoading(false); });
    return () => { cancelled = true; };
  }, [selectedGroupId, periodRefresh]);

  const groupRows = groups.map((group) => ({
    ...group,
    frequencyLabel: formatFrequency(group.payFrequency),
    statusLabel: group.status === 'active' ? 'Active' : 'Inactive',
  }));
  const periodRows = periods.map((period) => ({
    ...period,
    periodLabelDisplay: period.periodLabel || '—',
    dateRange: `${formatDate(period.periodStartDate)} – ${formatDate(period.periodEndDate)}`,
    paymentDateDisplay: formatDate(period.paymentDate),
  }));

  const openCreateGroup = () => {
    setGroupDraft(emptyGroupDraft);
    setSubmitted(false);
    setGroupModal('create');
  };
  const openEditGroup = () => {
    if (!selectedGroup) return;
    setGroupDraft({ name: selectedGroup.name, code: selectedGroup.code, payFrequency: selectedGroup.payFrequency });
    setSubmitted(false);
    setGroupModal('edit');
  };
  const openCreatePeriod = () => {
    setPeriodDraft(emptyPeriodDraft);
    setSubmitted(false);
    setPeriodModal('create');
  };
  const openEditPeriod = () => {
    if (!selectedPeriod) return;
    setPeriodDraft({
      periodStartDate: selectedPeriod.periodStartDate,
      periodEndDate: selectedPeriod.periodEndDate,
      paymentDate: selectedPeriod.paymentDate,
      periodLabel: selectedPeriod.periodLabel ?? '',
    });
    setSubmitted(false);
    setPeriodModal('edit');
  };

  const saveGroup = async () => {
    if (saving) return;
    setSubmitted(true);
    if (!groupDraft.name.trim() || !groupDraft.code.trim() || !groupDraft.payFrequency) return;
    setSaving(true);
    setGroupError(null);
    try {
      if (groupModal === 'create') {
        await createPayGroup({
          organisationId,
          name: groupDraft.name.trim(),
          code: groupDraft.code.trim(),
          payFrequency: groupDraft.payFrequency,
        });
      } else if (selectedGroup) {
        await updatePayGroup(selectedGroup.payGroupId, {
          name: groupDraft.name.trim(),
          payFrequency: groupDraft.payFrequency,
        });
      }
      setGroupModal(null);
      setGroupRefresh((value) => value + 1);
    } catch (reason) {
      setGroupError(reason instanceof Error ? reason.message : 'Failed to save Pay Group.');
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async () => {
    if (saving || !deactivationTarget) return;
    setSaving(true);
    setGroupError(null);
    try {
      await updatePayGroup(deactivationTarget.payGroupId, { status: 'inactive' });
      setDeactivationTarget(null);
      setSelectedGroup(null);
      setGroupRefresh((value) => value + 1);
    } catch (reason) {
      setGroupError(reason instanceof Error ? reason.message : 'Failed to deactivate Pay Group.');
    } finally {
      setSaving(false);
    }
  };

  const savePeriod = async () => {
    if (saving) return;
    setSubmitted(true);
    if (!selectedGroup || !periodDraft.periodStartDate || !periodDraft.periodEndDate || !periodDraft.paymentDate) return;
    if (periodDraft.periodEndDate < periodDraft.periodStartDate) return;
    setSaving(true);
    setPeriodError(null);
    try {
      const request = {
        periodStartDate: periodDraft.periodStartDate,
        periodEndDate: periodDraft.periodEndDate,
        paymentDate: periodDraft.paymentDate,
        periodLabel: periodDraft.periodLabel.trim() || null,
      };
      if (periodModal === 'create')
        await createPayPeriod(selectedGroup.payGroupId, request);
      else if (selectedPeriod)
        await updatePayPeriod(selectedPeriod.payPeriodId, request);
      setPeriodModal(null);
      setPeriodRefresh((value) => value + 1);
      setGroupRefresh((value) => value + 1);
    } catch (reason) {
      setPeriodError(reason instanceof Error ? reason.message : 'Failed to save Pay Period.');
    } finally {
      setSaving(false);
    }
  };

  if (availableRecords.length === 0)
    return <EmptyState title="No Business Records available" description="Create an active child Business Record before configuring payroll." />;

  return (
    <div className="mgo-stack mgo-gap-l">
      <div className="mgo-split mgo-align-end">
        <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-m">
          <SelectField
            label="Business Record"
            value={organisationId}
            options={availableRecords.map((record) => ({ label: record.name, value: record.orgId }))}
            searchable={false}
            onChange={(value) => {
              setOrganisationId(String(value ?? ''));
              setSelectedGroup(null);
            }}
          />
          <SelectField
            label="Status"
            value={status}
            options={statusOptions}
            searchable={false}
            onChange={(value) => {
              setStatus(value as PayGroupStatus);
              setSelectedGroup(null);
            }}
          />
        </div>
        <Button label="Add Pay Group" onClick={openCreateGroup} />
      </div>

      {groupError && (
        <Callout
          tone="critical"
          title="Payroll configuration error"
          action={(
            <Button
              label="Retry"
              size="small"
              variant="secondary"
              onClick={() => {
                setGroupError(null);
                setGroupRefresh((value) => value + 1);
              }}
            />
          )}
        >
          {groupError}
        </Callout>
      )}

      {loading ? (
        <div className="mgo-cluster" role="status" aria-live="polite">
          <Spinner />
          <span>Loading Pay Groups…</span>
        </div>
      ) : groups.length === 0 ? (
        <EmptyState
          title="No Pay Groups yet"
          description={`No ${status === 'active' ? 'active' : 'inactive'} Pay Groups exist for this Business Record.`}
          actions={status === 'active' ? [<Button key="add" label="Add Pay Group" size="small" onClick={openCreateGroup} />] : []}
        />
      ) : (
        <Table
          value={groupRows}
          columns={groupColumns}
          onRowClick={(row) => setSelectedGroup(row as PayGroupDto)}
          stripedRows
          testId="pay-groups-table"
        />
      )}

      {selectedGroup && (
        <Panel
          title={
            <div className="mgo-split">
              <div className="mgo-cluster">
                <span className="mgo-text-m mgo-weight-strong">{selectedGroup.name}</span>
                <Tag label={selectedGroup.status === 'active' ? 'Active' : 'Inactive'} tone={selectedGroup.status === 'active' ? 'positive' : 'neutral'} />
              </div>
              <div className="mgo-cluster">
                <Button label="Edit Pay Group" variant="secondary" size="small" disabled={!selectedGroup.meta.actions.canEdit} onClick={openEditGroup} />
                <Button label="Deactivate" variant="secondary" size="small" disabled={!selectedGroup.meta.actions.canDeactivate || saving} onClick={() => setDeactivationTarget(selectedGroup)} />
                <Button label="Add Pay Period" size="small" disabled={!selectedGroup.meta.actions.canAddPayPeriod} onClick={openCreatePeriod} />
              </div>
            </div>
          }
        >
          <div className="mgo-stack mgo-gap-l">
            <div className="mgo-cluster">
              <span className="mgo-text-s"><strong>Code:</strong> {selectedGroup.code}</span>
              <span className="mgo-text-s"><strong>Frequency:</strong> {formatFrequency(selectedGroup.payFrequency)}</span>
            </div>
            {periodError ? (
              <Callout
                tone="critical"
                title="Pay Period error"
                action={(
                  <Button
                    label="Retry Pay Periods"
                    size="small"
                    variant="secondary"
                    onClick={() => {
                      setPeriodError(null);
                      setPeriodRefresh((value) => value + 1);
                    }}
                  />
                )}
              >
                {periodError}
              </Callout>
            ) : periodsLoading ? (
              <div className="mgo-cluster" role="status"><Spinner /><span>Loading Pay Periods…</span></div>
            ) : periods.length === 0 ? (
              <EmptyState title="No Pay Periods yet" description="Add the first explicit Pay Period for this Pay Group." />
            ) : (
              <>
                <Table
                  value={periodRows}
                  columns={periodColumns}
                  onRowClick={(row) => setSelectedPeriod(row as PayPeriodDto)}
                  stripedRows
                  testId="pay-periods-table"
                />
                <Button label="Edit selected Pay Period" variant="secondary" size="small" disabled={!selectedPeriod} onClick={openEditPeriod} />
              </>
            )}
          </div>
        </Panel>
      )}

      <Modal
        visible={deactivationTarget !== null}
        header="Deactivate Pay Group"
        onHide={() => setDeactivationTarget(null)}
        footer={
          <>
            <Button label="Cancel" variant="secondary" onClick={() => setDeactivationTarget(null)} />
            <Button label="Deactivate Pay Group" loading={saving} disabled={saving} onClick={deactivate} />
          </>
        }
      >
        <p>
          Deactivate <strong>{deactivationTarget?.name}</strong>? It will be removed from active selection.
          Historical data remains available.
        </p>
      </Modal>

      <Modal
        visible={groupModal !== null}
        header={groupModal === 'create' ? 'Add Pay Group' : 'Edit Pay Group'}
        onHide={() => setGroupModal(null)}
        footer={
          <>
            <Button label="Cancel" variant="secondary" onClick={() => setGroupModal(null)} />
            <Button label="Save Pay Group" loading={saving} disabled={saving} onClick={saveGroup} />
          </>
        }
      >
        <div className="mgo-stack mgo-gap-l">
          <div className="mgo-grid mgo-grid-cols-tablet-12 mgo-gap-m">
            <div className="mgo-col-span-tablet-8">
              <TextField
                label="Name"
                value={groupDraft.name}
                status={submitted && !groupDraft.name.trim() ? 'critical' : undefined}
                statusText={submitted && !groupDraft.name.trim() ? 'Name is required.' : undefined}
                onChange={(event) => setGroupDraft((draft) => ({ ...draft, name: event.target.value }))}
              />
            </div>
            <div className="mgo-col-span-tablet-4">
              <TextField
                label="Code"
                value={groupDraft.code}
                readOnly={groupModal === 'edit'}
                status={submitted && !groupDraft.code.trim() ? 'critical' : undefined}
                statusText={submitted && !groupDraft.code.trim() ? 'Code is required.' : undefined}
                onChange={(event) => setGroupDraft((draft) => ({ ...draft, code: event.target.value }))}
              />
            </div>
          </div>
          <SelectField
            label="Pay Frequency"
            value={groupDraft.payFrequency || null}
            options={frequencyOptions}
            searchable={false}
            status={submitted && !groupDraft.payFrequency ? 'critical' : ''}
            statusText={submitted && !groupDraft.payFrequency ? 'Pay Frequency is required.' : undefined}
            onChange={(value) => setGroupDraft((draft) => ({ ...draft, payFrequency: value as PayFrequency }))}
          />
        </div>
      </Modal>

      <Modal
        visible={periodModal !== null}
        header={periodModal === 'create' ? 'Add Pay Period' : 'Edit Pay Period'}
        onHide={() => setPeriodModal(null)}
        footer={
          <>
            <Button label="Cancel" variant="secondary" onClick={() => setPeriodModal(null)} />
            <Button label="Save Pay Period" loading={saving} disabled={saving} onClick={savePeriod} />
          </>
        }
      >
        <div className="mgo-stack mgo-gap-l">
          <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-m">
            <DateField
              label="Period Start Date"
              dateFormat="ymd"
              fullWidth
              value={periodDraft.periodStartDate}
              status={submitted && !periodDraft.periodStartDate ? 'critical' : undefined}
              statusText={submitted && !periodDraft.periodStartDate ? 'Start date is required.' : undefined}
              onChange={(value) => setPeriodDraft((draft) => ({ ...draft, periodStartDate: value }))}
            />
            <DateField
              label="Period End Date"
              dateFormat="ymd"
              fullWidth
              min={periodDraft.periodStartDate || undefined}
              value={periodDraft.periodEndDate}
              status={submitted && (!periodDraft.periodEndDate || periodDraft.periodEndDate < periodDraft.periodStartDate) ? 'critical' : undefined}
              statusText={submitted && !periodDraft.periodEndDate
                ? 'End date is required.'
                : submitted && periodDraft.periodEndDate < periodDraft.periodStartDate
                  ? 'End date must be on or after the start date.'
                  : undefined}
              onChange={(value) => setPeriodDraft((draft) => ({ ...draft, periodEndDate: value }))}
            />
          </div>
          <DateField
            label="Payment Date"
            dateFormat="ymd"
            fullWidth
            value={periodDraft.paymentDate}
            status={submitted && !periodDraft.paymentDate ? 'critical' : undefined}
            statusText={submitted && !periodDraft.paymentDate ? 'Payment date is required.' : undefined}
            onChange={(value) => setPeriodDraft((draft) => ({ ...draft, paymentDate: value }))}
          />
          <TextField
            label="Period Label"
            helpText="Optional, for example PP19 - FY2027."
            value={periodDraft.periodLabel}
            onChange={(event) => setPeriodDraft((draft) => ({ ...draft, periodLabel: event.target.value }))}
          />
        </div>
      </Modal>
    </div>
  );
};
